import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
	catchError,
	combineLatest,
	debounceTime,
	distinctUntilChanged,
	EMPTY,
	filter,
	forkJoin,
	from,
	map,
	Observable,
	of,
	startWith,
	switchMap,
	tap,
} from 'rxjs';
import { AppRoutes } from 'src/app/app-routes';
import { CustomerDataUI, ValidateCustomerDataUI } from 'src/app/interfaces/customer-data.interface';
import { PurposesByType } from 'src/app/interfaces/get-purposes';
import {
	InitialParametersUI,
	SalesLevelModel,
} from 'src/app/interfaces/initial-parameters.interface';
import { ClickEventTags, PageEventTags } from 'src/app/services/gtm/gtm-types';
import { GtmService } from 'src/app/services/gtm/gtm.service';
import { LoaderService } from 'src/app/services/loader/loader.service';
import { RecaptchaService } from 'src/app/services/recaptcha/recaptcha.service';
import { StepperService } from 'src/app/services/stepper/stepper.service';
import { StoreService } from 'src/app/services/store/store.service';
import { Patterns, RecaptchaActions } from 'src/app/utils/constants';
import {
	buildPrivacyTermsGroup,
	createUserRutControl,
	emailRegexValidator,
	ensureUserRut,
	phoneValidationsRegex,
	rutValidator,
} from 'src/app/utils/form-validators.utils';
import { mapper } from 'src/app/utils/mapper';
import {
	buildRequestGenerateOtp,
	buildUrlSimulator,
	findSelectedSalesRange,
	isLegalEntity,
	mapToGtmExtras,
	selectIdFormPurpose,
	selectPurposes,
} from 'src/app/utils/utils';

@Component({
	selector: 'app-company-data',
	templateUrl: './company-data.component.html',
	styleUrls: ['./company-data.component.scss'],
})
export class CompanyDataComponent implements OnInit {
	showWhatsappBtn = false;

	companyDataForm: FormGroup = this.fb.nonNullable.group({
		companyRut: ['', [Validators.required, Validators.maxLength(12), rutValidator()]],
		phoneNumber: [
			'',
			[
				Validators.required,
				Validators.minLength(12),
				Validators.pattern(Patterns.phone),
				phoneValidationsRegex(),
			],
		],
		email: ['', [Validators.required, Validators.email, emailRegexValidator()]],
		salesLevel: ['', [Validators.required]],
		privacyTerms: this.fb.group({}),
	});

	private readonly isPJ$ = this.companyRutControl.valueChanges.pipe(
		startWith(this.companyRutControl.value),
		map(isLegalEntity),
		distinctUntilChanged()
	);

	readonly viewModel$ = combineLatest([
		this.storeService.initialParameters$,
		this.storeService.purposes$,
		this.isPJ$,
	]).pipe(
		filter(([parameters, purposes]) => parameters != null && purposes != null),
		tap(([, purposes, isPJ]) => {
			this.syncUserRutControl(isPJ);
			this.syncPrivacyTermsControls(isPJ, purposes as PurposesByType);
		}),
		map(([parameters, purposes, isPJ]) => ({
			parameters: parameters as InitialParametersUI,
			activePurposes: selectPurposes(isPJ, purposes as PurposesByType),
			idForm: selectIdFormPurpose(isPJ, purposes as PurposesByType),
		}))
	);

	constructor(
		private readonly recaptchaService: RecaptchaService,
		private readonly loaderService: LoaderService,
		private readonly storeService: StoreService,
		private readonly fb: FormBuilder,
		private readonly stepperService: StepperService,
		private readonly router: Router,
		private readonly destroyRef: DestroyRef,
		private readonly gtmService: GtmService
	) {}

	get companyRutControl() {
		return this.companyDataForm.controls.companyRut;
	}

	get userRutControl() {
		return this.companyDataForm.get('userRut');
	}

	get phoneControl() {
		return this.companyDataForm.controls.phoneNumber;
	}
	get emailControl() {
		return this.companyDataForm.controls.email;
	}

	ngOnInit(): void {
		this.setupErrorTrigger(this.companyRutControl);
		this.setupErrorTrigger(this.emailControl);
		this.gtmService.handlerEvent(PageEventTags.companyData);
	}

	private syncUserRutControl(isPJ: boolean): void {
		const hasUserRut = this.companyDataForm.contains('userRut');

		if (isPJ && !hasUserRut) {
			const userRut = createUserRutControl(this.fb);
			this.companyDataForm.addControl('userRut', userRut);
			this.setupErrorTrigger(userRut);
			return;
		}

		if (!isPJ && hasUserRut) {
			this.companyDataForm.removeControl('userRut');
		}
	}

	private syncPrivacyTermsControls(isPJ: boolean, purposes: PurposesByType): void {
		this.companyDataForm.removeControl('privacyTerms');
		const list = selectPurposes(isPJ, purposes);
		this.companyDataForm.addControl('privacyTerms', buildPrivacyTermsGroup(list, this.fb));
	}

	private setupErrorTrigger(control: AbstractControl, delayMs = 1000): void {
		control.statusChanges
			.pipe(debounceTime(delayMs), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
			.subscribe(status => {
				if (status === 'INVALID') {
					control.markAsTouched({ onlySelf: true });
				}
			});
	}

	onSendDataForValidation(parameters: InitialParametersUI, idForm: string): void {
		if (this.companyDataForm.invalid) {
			this.companyDataForm.markAllAsTouched();
			return;
		}
		this.validateFlow$(parameters, idForm).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
	}

	private validateFlow$(parameters: InitialParametersUI, idForm: string): Observable<void> {
		const formData: CustomerDataUI = this.companyDataForm.getRawValue();

		const registerPurposesRequest = mapper.requestRegisterPurposes(
			formData.companyRut,
			idForm,
			this.companyDataForm.get('privacyTerms')?.value
		);

		return this.pushGtm$(parameters.salesLevels, formData).pipe(
			switchMap(() => this.recaptchaService.executeAction(RecaptchaActions.checkPreapprovalValidity)),
			map(token => mapper.requestValidateData(ensureUserRut(formData), token)),
			switchMap(request =>
				forkJoin({
					validations: this.storeService.validateData(request),
					registerResult: this.storeService
						.registerPurposes(registerPurposesRequest)
						.pipe(catchError(() => of(null))),
				})
			),
			tap(() => this.saveDataAndAdvance(ensureUserRut(formData))),
			switchMap(({ validations }) =>
				this.handleValidationResult$(validations, parameters.simulatorUrl, formData.companyRut)
			),
			map(() => void 0)
		);
	}

	private pushGtm$(salesLevels: SalesLevelModel[], formData: CustomerDataUI): Observable<void> {
		const selectedRange = findSelectedSalesRange(salesLevels, formData.salesLevel);

		return from(mapToGtmExtras(formData.companyRut, formData.email, selectedRange)).pipe(
			tap(extras => this.gtmService.setExtras(extras)),
			tap(() => this.gtmService.handlerEvent(ClickEventTags.validateData)),
			map(() => void 0)
		);
	}

	private saveDataAndAdvance(formData: CustomerDataUI): void {
		this.stepperService.nextStep();
		this.storeService.setCustomerData(formData);
	}

	private handleValidationResult$(
		validations: ValidateCustomerDataUI,
		simulatorUrl: string,
		companyRut: string
	): Observable<void> {
		if (!validations.approvedRisk) {
			this.loaderService.show();
			window.location.assign(buildUrlSimulator(simulatorUrl, validations.uuid, validations.id));
			return EMPTY;
		}

		if (!validations.applyCte) {
			return this.storeService.generateSendOtp(buildRequestGenerateOtp(companyRut)).pipe(
				tap(() => this.router.navigate([AppRoutes.OTP])),
				map(() => void 0)
			);
		}

		return this.storeService.generateConsentTaxFolder(companyRut).pipe(
			tap(() => this.router.navigate([AppRoutes.TAX_FOLDER])),
			map(() => void 0)
		);
	}
}
